<script setup lang="ts">
import type { JobFilterComparison, MultiselectOption } from '@ironsight/types'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { computed, onMounted, ref, watch } from 'vue'

interface Props {
  value: number | string
  label?: string
  canBeEmpty?: boolean
  comparison?: JobFilterComparison
  comparisonOptions?: MultiselectOption[]
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  canBeEmpty: false,
  comparison: 'Equals',
  comparisonOptions: () => [],
})

const emit = defineEmits<{
  (e: 'input', value: number | string): void
  (e: 'operation-change', value: JobFilterComparison): void
}>()

function handleInputChange(value: string) {
  if (isEmptySearch.value) {
    return
  }
  const numValue = value === '' ? '' : Number(value)
  emit('input', numValue)
}

const lastNonEmptyComparison = ref<JobFilterComparison>('Equals')
const hasIsEmptyOption = computed(
  () => props.comparisonOptions?.some((option) => option.value === 'IsEmpty') || props.canBeEmpty,
)
const isEmptySearch = computed(() => hasIsEmptyOption.value && props.comparison === 'IsEmpty')

watch(
  () => props.comparison,
  (newComparison) => {
    if (newComparison && newComparison !== 'IsEmpty') {
      lastNonEmptyComparison.value = newComparison
    }
  },
  { immediate: true },
)

function handleEmptyToggle(value: boolean) {
  if (value) {
    emit('input', '')
    emit('operation-change', 'IsEmpty')
    return
  }
  emit('operation-change', lastNonEmptyComparison.value)
}

type VsTextInputInstance = InstanceType<typeof VsTextInput> & {
  focus?: () => void
}

const searchInputRef = ref<VsTextInputInstance | null>(null)

onMounted(() => {
  if (!isEmptySearch.value) {
    searchInputRef.value?.focus?.()
  }
})
</script>

<template>
  <div class="filter-search">
    <vs-text-input
      ref="searchInputRef"
      :step="1"
      :label="props.label || 'Search'"
      :value="props.value"
      :disabled="isEmptySearch"
      type="number"
      autofocus
      append-icon="search"
      @input="handleInputChange"
    >
      <template #append>
        <v-icon>search</v-icon>
      </template>
    </vs-text-input>

    <div v-if="hasIsEmptyOption" class="empty-toggle">
      <v-checkbox
        :input-value="isEmptySearch"
        label="Is empty"
        hide-details
        dense
        @change="handleEmptyToggle"
      />
    </div>
  </div>
</template>

<style scoped>
.filter-search {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.empty-toggle {
  margin-top: 8px;
}
</style>
